import React, { useContext, useState, useEffect, useMemo } from "react";
import styled, { css } from "styled-components";
import { TickList } from "../components/TickList";
import { ProductGallery } from "../components/ProductGallery";
import { ProductPrice } from "../components/ProductPrice";
import { ProductStars } from "../components/ProductStars";
import BasketSmall from "../components/Icons/BasketSmall";
import Section from "../components/Section";
import Container from "../components/Container";
import Grid from "../components/Grid";
import { QuoteBasketContext } from "../context/QuoteBasketContext";
import { isBrowser } from "../utils/isBrowser";
import { useStaticQuery, graphql } from "gatsby";
import { Helmet } from "react-helmet";
import randomstring from "randomstring";
import { navigateToQuoteForm } from "./QuoteFormAndSafesitePromise";
import { HtmlContent } from "./Content";
import PricesMayVary from "./PricesMayVary";

const CTAStyles = styled.div`
  ${({ ...props }) => css`
    margin-top: 24px;

    button {
      margin-bottom: 1rem;
      min-width: 250px;
      text-align: center;
      box-shadow: 0 1px 1px rgba(0, 0, 0, 0.15), 0 2px 2px rgba(0, 0, 0, 0.15),
        0 4px 4px rgba(0, 0, 0, 0.15), 0 8px 8px rgba(0, 0, 0, 0.15);
      display: flex;
      justify-content: center;
      align-items: center;

      svg {
        margin-right: 8px;
        height: 24px;
      }
    }
    @media (min-width: ${props.theme.breakpoints.lg}) {
      display: flex;
      align-items: center;
      margin-top: 32px;

      button {
        flex-shrink: 0;
        margin-bottom: 0;
        margin-right: 1rem;
        min-width: 300px;
      }
    }
  `}
`;

const StickyBarStyles = styled.div`
  ${({ ...props }) => css`
    background: #fff;
    position: fixed;
    bottom: ${(props.$visible ? "0" : "-96px")};
    opacity: ${(props.$visible ? "1" : "0")};
    left: 0;
    width: 100%;
    z-index: 10;
    box-shadow: 0px -4px 17px 0px rgba(68, 68, 68, 0.55);
    transition: all ease 0.6s;
    will-change: bottom;
    .action button {
      text-align: center;
      box-shadow: 0 1px 1px rgb(0 0 0 / 15%), 0 2px 2px rgb(0 0 0 / 15%),
        0 4px 4px rgb(0 0 0 / 15%), 0 8px 8px rgb(0 0 0 / 15%);
      display: flex;
      justify-content: center;
      align-items: center;

      svg {
        margin-right: 8px;
        height: 24px;
      }
    }

    @media (max-width: 991px) {
      padding: 24px 16px;

      .title {
        display: none;
      }

      .action button {
        width: 100%;
        max-width: 240px;
      }
    }

    @media (max-width: 360px) {
      .action button {
        max-width: 200px;
      }
    }

    @media (min-width: ${props.theme.breakpoints.lg}) {
      padding: 24px 0 8px 0;
      bottom: ${(props.$visible ? "0" : "-137px")};
      min-height: 137px;
      .container {
        display: flex;
        justify-content: space-between;
        padding: 0 64px;
      }

      .action {
        text-align: right;

        a,
        button {
          min-width: 300px;
          margin-bottom: 16px;
        }
      }
    }

    @media (min-width: ${props.theme.breakpoints.xl}) {
      .container {
        padding: 0px 120px;
      }
    }
  `}
`;

export const ProductGalleryAndPricing = ({
  product,
  page,
  handleReviewLinkClick,
  showStickyBar
}) => {
  let siteSettings = {
    siteSettings: {
      siteSettings: {
        siteSettings: {
          enableQuoteBasketSiteWide: true
        }
      }
    }
  }
  if (!process.env.STORYBOOK) {
    siteSettings = useStaticQuery(graphql`
      query ProductGalleryAndPricingQuery {
        siteSettings: wp {
          siteSettings {
            siteSettings {
              enableQuoteBasketSiteWide
            }
          }
        }
      }
    `);
  }

  const quoteBasketGloballyEnabled = siteSettings.siteSettings.siteSettings.siteSettings.enableQuoteBasketSiteWide;
  const quoteBasketProductEnabled = !product.disableAddToQuoteBasketFunctionality;

  const { quoteBasketItems, setQuoteBasketItems, setQuoteBasketIsActive } = useContext(QuoteBasketContext);
  // Handle check if current product is in current quote
  const [isInQuote, setIsInQuote] = useState(false);

  useEffect(() => {
    const updatedQuoteBasketItems = quoteBasketItems;
    if (
      isBrowser &&
      updatedQuoteBasketItems &&
      updatedQuoteBasketItems.length > 0 &&
      updatedQuoteBasketItems.some(
        (quoteBasketItem) => quoteBasketItem.id === page.id
      )
    ) {
      setIsInQuote(true);
    } else {
      setIsInQuote(false);
    }
  }, [quoteBasketItems, page.id, isBrowser]);
  
  const handleAddToQuote = () => {
    // Add item to provider
    const updatedQuoteBasketItems = quoteBasketItems;

    // Check if current product is NOT in current array
    if (
      isBrowser &&
      !updatedQuoteBasketItems.some(
        (quoteBasketItem) => quoteBasketItem.id === page.id
      )
    ) {
      // Add new item to start of array
      updatedQuoteBasketItems.unshift({
        name: page.title,
        id: page.id,
        quantity: 1,
        image: product.productGallery[0].mediaItemUrl,
        buyOrHire: "buy",
        quantityType: "unit",
        url: page.uri,
        hireDuration: 1,
      })

      setQuoteBasketItems(updatedQuoteBasketItems)
      setIsInQuote(true)
      sessionStorage.setItem(
        "quoteBasketItems",
        JSON.stringify(updatedQuoteBasketItems)
      )

      // Open Quote basket modal
      setQuoteBasketIsActive(true)
    }
  }

  const handleRemoveFromQuote = () => {
    // Open Quote basket modal
    setQuoteBasketIsActive(true)
  }

  const rand = useMemo(() => randomstring.generate(5), []);

  // Handle scroll/sticky bar logic
  const [topButtonVisibleState, setTopButtonVisibleState] = useState(true)
  const [bottomButtonVisibleState, setBottomButtonVisibleState] = useState(false)
  const [overlappingFooter, setOverlappingFooter] = useState(false)

  useEffect(() => {
    // Observer to detect when the top quote button is out of view
    const topButtonObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTopButtonVisibleState(true)
        } else {
          setTopButtonVisibleState(false)
        }
      },
      { threshold: 0 }
    )

    // Observer to detect when the bottom quote button is fully visible
    const bottomButtonObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setBottomButtonVisibleState(true)
        } else {
          setBottomButtonVisibleState(false)
        }
      },
      { threshold: 0 }
    )

    const bottomQuoteButtons = document.querySelectorAll(".quote-submit-btn")
    let lastButton
    if (bottomQuoteButtons.length > 0) {
      lastButton = bottomQuoteButtons[bottomQuoteButtons.length - 1]
    }

    // Set the top quote button once it's mounted and apply observer
    const topButton = document.getElementById(`${rand}_top-quote-button`)

    if (topButton) {
      topButtonObserver.observe(topButton)
    }

    if (lastButton) {
      bottomButtonObserver.observe(lastButton)
    }

    const setOverlappingFooterSate = () => {
      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight) {
        setOverlappingFooter(true)
      } else {
        setOverlappingFooter(false)
      }
    }

    window.addEventListener('scroll', setOverlappingFooterSate);

    // Cleanup on unmount
    return () => {
      if (topButton) {
        topButtonObserver.unobserve(topButton)
      }

      if (lastButton) {
        bottomButtonObserver.unobserve(lastButton)
      }

      window.removeEventListener('scroll', setOverlappingFooterSate)
    }
  }, [rand])

  return (
    <>
      <Helmet
        bodyAttributes={{
          class: showStickyBar && !topButtonVisibleState && !bottomButtonVisibleState ? 'hide-usp' : '',
        }}
      />
      <Section>
        <Container>
          <Grid columnGap="loose">
            <Grid item md="5" xl="6" columnGap="loose">
              {/* Gallery */}
              <ProductGallery
                images={product.productGallery}
                threeDEmbed={product.threeDView.threeDViewEmbed}
              />
            </Grid>
            <Grid item md="7" xl="6" columnGap="loose">
              {/* Title */}
              <h1>
                <HtmlContent htmlString={page.title} />
              </h1>
              {/* Reviews */}
              <ProductStars
                productId={page.id}
                onClick={handleReviewLinkClick}
              />
              {/* USPS */}
              {product.productMeta?.usps && (
                <TickList tickList={product.productMeta.usps} />
              )}
              {/* Pricing */}
              {(product.productMeta?.hirePrice?.price || product.productMeta?.hirePrice?.priceOnApplication || product.productMeta?.buyPrice?.price || product.productMeta?.buyPrice?.priceOnApplication) && (
                <ProductPrice
                  hirePrice={product.productMeta.hirePrice}
                  buyPrice={product.productMeta.buyPrice}
                />
              )}
              {/* CTA button */}
              <CTAStyles id={`${rand}_top-quote-button`}>
                {quoteBasketProductEnabled && quoteBasketGloballyEnabled ? (
                  <button
                    className="button"
                    onClick={() =>
                      isInQuote
                        ? handleRemoveFromQuote()
                        : handleAddToQuote()
                    }
                  >
                    <BasketSmall />
                    {isInQuote ? "Remove/Edit quote" : "Add to quote"}
                  </button>
                ) : (
                  <button
                    className="button"
                    onClick={navigateToQuoteForm}
                  >
                    Request a free quote
                  </button>
                )}

                {!product.hidePricesMayVaryText && (
                  <PricesMayVary />
                )}
              </CTAStyles>
            </Grid>
          </Grid>
        </Container>
      </Section>
      {showStickyBar && <StickyBarStyles $visible={!topButtonVisibleState && !bottomButtonVisibleState && !overlappingFooter}>
        <Container>
          <div className="container">
            <div className="title">
              <h2>
                <HtmlContent htmlString={page.title} />
              </h2>
              <ProductStars
                productId={page.id}
                onClick={handleReviewLinkClick}
              />
            </div>
            <div className="action">
              {quoteBasketGloballyEnabled && quoteBasketProductEnabled ? (
                <button
                  className="button"
                  onClick={() =>
                    isInQuote
                      ? handleRemoveFromQuote()
                      : handleAddToQuote()
                  }
                >
                  <BasketSmall />
                  {isInQuote ? "Remove from quote" : "Add to quote"}
                </button>
              ) : (
                <button
                  className="button"
                  onClick={navigateToQuoteForm}
                >
                  Request a free quote
                </button>
              )}
            </div>
          </div>
        </Container>
      </StickyBarStyles>}
    </>
  )
};
